import { gql } from "@apollo/client";
import useSWR from "swr";

import { getSubsquidGraphClient } from "lib/subgraph";
import { Distribution } from "sdk/types/subsquid";

/**
 * The shape this query actually returns.
 *
 * The generated `Distribution` schema type carries a flat `transactionHash`,
 * but this query selects the `transaction { timestamp hash }` relation (and
 * orders by `transaction_timestamp_DESC`), so the response nests it. Typing the
 * fetch against the selection set keeps consumers honest without editing the
 * generated file, which is overwritten on regeneration.
 */
export type IncentiveDistribution = Pick<Distribution, "id" | "typeId" | "amounts" | "amountsInUsd" | "tokens"> & {
  transaction: { timestamp: number; hash: string };
};

const USER_INCENTIVE_QUERY = gql`
  query userIncentiveData($account: String!) {
    distributions(orderBy: transaction_timestamp_DESC, where: { receiver_eq: $account }, limit: 1000) {
      id
      typeId
      amounts
      amountsInUsd
      tokens
      transaction {
        timestamp
        hash
      }
    }
  }
`;

export default function useUserIncentiveData(chainId: number, account?: string) {
  const squidClient = getSubsquidGraphClient(chainId);
  const userIncentiveDataCacheKey =
    chainId && squidClient && account ? [chainId, "useUserIncentiveData", account] : null;

  async function fetchUserIncentiveData(): Promise<IncentiveDistribution[] | undefined> {
    if (!account) {
      return [];
    }

    const response = await squidClient!.query({
      query: USER_INCENTIVE_QUERY,
      variables: { account: account },
    });

    return response.data?.distributions as IncentiveDistribution[] | undefined;
  }

  const { data, error } = useSWR<IncentiveDistribution[] | undefined>(userIncentiveDataCacheKey, {
    fetcher: fetchUserIncentiveData,
  });

  return { data, error };
}
